/*  ========================================================================
    # Sections - Quotes
    ========================================================================  */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import useLocalization from '../../hooks/useLocalization';
import useTranslation from '../../hooks/useTranslation';
import tailwindConfig from '../../../../tailwind.config';
import bem from '../../helpers/bem';
import LazyImage from '../../components/LazyImage';
import LazySource from '../../components/LazySource';

const Quotes = ({ heading, entries }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const __ = useTranslation();

  // Icons
  const localize = useLocalization();
  const iconArrowLeft = `${localize('svgSprite')}#arrow-left`;
  const iconArrowRight = `${localize('svgSprite')}#arrow-right`;

  // If there are fewer than 5 entries, the array is padded with existing ones.
  const sliderEntries = useMemo(() => {
    let tempEntries = [];

    if (entries.length > 1 && entries.length < 5) {
      tempEntries = [...entries];
      while (tempEntries.length < 5) tempEntries.push(...entries);
    } else {
      tempEntries = entries;
    }

    return tempEntries;
  }, [entries]);

  // Get the slide offset value.
  const getSlideOffset = index => {
    const delta = index - activeSlide;

    if (activeSlide === 0 && index === sliderEntries.length - 1) return -1;
    if (activeSlide === 0 && index === sliderEntries.length - 2) return -2;
    if (activeSlide === 1 && index === sliderEntries.length - 1) return -2;

    if (activeSlide === sliderEntries.length - 1 && index === 0) return 1;
    if (activeSlide === sliderEntries.length - 1 && index === 1) return 2;

    if (activeSlide === sliderEntries.length - 2 && index === 0) return 2;

    if (delta <= -3) return -2;
    if (delta >= 3) return 2;

    return delta;
  };

  // Navigate Left
  const navigateLeft = () => {
    if (activeSlide > 0) setActiveSlide(activeSlide - 1);
    else setActiveSlide(sliderEntries.length - 1);
  };

  // Navigate Right
  const navigateRight = () => {
    if (activeSlide < sliderEntries.length - 1) setActiveSlide(activeSlide + 1);
    else setActiveSlide(0);
  };

  // Add swipe handlers.
  const swipeHandlers = useSwipeable({
    onSwipedRight: navigateLeft,
    onSwipedLeft: navigateRight
  });

  return (
    <div className='container'>
      <div className={bem('s-quotes', 'heading-container')}>
            <h2
              className={`${bem('s-quotes', 'heading')} h3`}
              dangerouslySetInnerHTML={{ __html: heading }}
            />
            {sliderEntries.length > 1 ? (
              <>
                <button
                  className={bem('c-button', '', { 'outline-black': true, icon: true })}
                  title={__('section-quotes-navigate-left')}
                  aria-label={__('section-quotes-navigate-left')}
                  onClick={navigateLeft}
                >
                  <svg viewBox='0 0 200 200'>
                    <use href={iconArrowLeft}></use>
                  </svg>
                </button>
                <button
                  className={bem('c-button', '', { 'outline-black': true, icon: true })}
                  title={__('section-quotes-navigate-right')}
                  aria-label={__('section-quotes-navigate-right')}
                  onClick={navigateRight}
                >
                  <svg viewBox='0 0 200 200'>
                    <use href={iconArrowRight}></use>
                  </svg>
                </button>
              </>
            ) : null}
          </div>

          <div className={bem('s-quotes', 'wrapper')} {...swipeHandlers}>
            {sliderEntries.map((entry, index) => (
              <div
                key={`${entry.name}-${index}`}
                className={bem('s-quotes', 'entry')}
                style={sliderEntries.length === 1 ? { opacity: 1 } : {}}
                data-offset={sliderEntries.length === 1 ? '' : getSlideOffset(index)}
              >
                <p className={bem('s-quotes', 'entry-text')}>{entry.quote}</p>
                <div className={bem('s-quotes', 'entry-person')}>
                  {entry.image ? (
                    <LazyImage
                      figureClass={bem('s-quotes', 'entry-person-image')}
                      defaultUrl={entry.image.sizes.thumbnail}
                      placeholderUrl={entry.image.sizes.placeholder}
                      alt={entry.image.alt}
                    >
                      <LazySource
                        srcSet={entry.image.sizes.thumbnail}
                        mediaMin={tailwindConfig.theme.screens.sm}
                      />
                    </LazyImage>
                  ) : null}
                  <div
                    className={`${bem('s-quotes', 'entry-person-description')}${entry.image ? '' : ' tw-text-center'
                      }`}
                  >
                    <strong>{entry.name}</strong>
                    {entry.position ? (
                      <span dangerouslySetInnerHTML={{ __html: entry.position }} />
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
    </div>
  );
};

Quotes.propTypes = {
  heading: PropTypes.string,
  entries: PropTypes.array
};

export default Quotes;
